import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import UAParser from 'ua-parser-js';
import { isFunction, isUndefined } from '../../utils/type-guards';
export type BasicDeviceInfo = {
  browser: string;
  osName: string;
  type: string;
};
export type DeviceInfo = BasicDeviceInfo & {
  hasSupportForWebauthn: boolean;
  initialCameraPermissionState?: PermissionState;
};

// UAParser device type can have an undefined type, because
// it could get executed on the server side. We assign a default
// device, just to avoid to make a lot of ifs
const DEFAULT_DEVICE_TYPE = 'unknown';
const DEFAULT_OS_TYPE = 'unknown';
const DEFAULT_BROWSER_TYPE = 'unknown';
const getCameraPermissionState = async (): Promise<PermissionState> => {
  if (isUndefined(navigator)) {
    return Promise.resolve('denied');
  }
  if (isUndefined(navigator.permissions)) {
    return Promise.resolve('denied');
  }
  if (!isFunction(navigator.permissions.query)) {
    return Promise.resolve('denied');
  }
  try {
    const res = await navigator.permissions.query({
      name: 'camera' as PermissionName
    });
    return res.state;
  } catch (_err) {
    return 'denied';
  }
};
export const getBasicDevice = () => {
  // return device without webauthn status for clients that don't care
  const uaParser = new UAParser();
  const device = uaParser.getDevice();
  const os = uaParser.getOS();
  const browser = uaParser.getBrowser();
  const info: BasicDeviceInfo = {
    browser: browser.name || DEFAULT_BROWSER_TYPE,
    osName: os.name || DEFAULT_OS_TYPE,
    type: device.type || DEFAULT_DEVICE_TYPE
  };
  return info;
};
const checkDeviceInfo = async () => {
  let hasSupportForWebauthn = false;
  if (typeof window !== 'undefined' && window.PublicKeyCredential) {
    try {
      hasSupportForWebauthn = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Error checking for WebAuthn support:', error);
    }
  }
  const basicInfo = getBasicDevice();
  return {
    hasSupportForWebauthn,
    ...basicInfo
  };
};
const useDeviceInfo = (onComplete, onError) => {
  const $ = _c(4);
  let t0;
  if ($[0] !== onComplete || $[1] !== onError) {
    t0 = () => {
      Promise.all([checkDeviceInfo(), getCameraPermissionState()]).then(t1 => {
        const [deviceInfo, initialCameraPermissionState] = t1;
        return onComplete({
          ...deviceInfo,
          initialCameraPermissionState
        });
      }).catch(onError);
    };
    $[0] = onComplete;
    $[1] = onError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(t0, t1);
};
export default useDeviceInfo;